<script lang="ts">
  import { ProgressiveImage } from '$mds/Image';
  import { humanizeDurationMinutes, toInteger } from '$lib/utils';
  import { dayjs } from '@metafy/dates';
  import Tag from '$mds/Tag';
  import Avatar from '$components/Avatar.svelte';

  type GuideItem = {
    id: string;
    objectID: string;
    slug: string;
    title: string;
    description: string;
    cover: string | null;
    seller_avatar: string;
    seller_name: string;
    game: string;
    game_logo: string | null;
    game_id: string | null;
    game_slug: string | null;
    total_reading_time_minutes: number;
    chapter_count: number;
    published_at: string;
    last_updated_at: string;
    price: string;
  };

  export let guide: GuideItem & { _formatted?: GuideItem };
  let className = 'sm:hover:bg-ondark-low';
  export { className as class };

  $: isNew = dayjs(guide?.published_at).isAfter(dayjs().subtract(1, 'week'));
  $: isUpdated = dayjs(guide?.last_updated_at).isAfter(dayjs().subtract(1, 'week'));
</script>

<a
  href="/guides/view/{guide?.slug}"
  class="focus-visible:bg-ondark-low focus-visible:ring-ondark-focused guide flex h-14 items-center justify-between space-x-3 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 sm:px-3 {className}"
  data-sveltekit-preload-code="hover"
  data-sveltekit-preload-data="off"
  on:keydown
  on:click
>
  <div class="relative flex min-w-0 flex-1 items-center justify-start gap-2">
    <ProgressiveImage
      src={guide.cover}
      wrapClass="shrink-0"
      class="aspect-video h-6 w-auto object-cover"
      sizes={{ xs: 64 }}
      alt="{guide.title} cover image"
      rounded="rounded"
    />
    {#if guide?.game}
      <span class="absolute translate-x-8 translate-y-3">
        <ProgressiveImage
          src={guide?.game_logo}
          wrapClass=" ring-[3px] ring-[#14141a] aspect-square size-6 object-cover bg-ondark-low"
          sizes={{ xs: 48 }}
          alt="{guide?.game} Logo"
          rounded="rounded-full"
        />
      </span>
    {/if}
    <div class="flex flex-col truncate">
      <h4 class="sm:divide-ondark-base flex items-end overflow-hidden sm:divide-x">
        <span class="highlight text-ondark-primary ml-4 inline-block truncate pr-2">
          {@html guide._formatted?.title ?? guide.title ?? ''}
        </span>
        <span class="text-ondark-subtle text-body-2xs hidden pl-2 sm:inline-block">
          <span class="highlight text-ondark-subtle truncate">
            {@html guide._formatted?.seller_name ?? guide.seller_name ?? ''}
          </span>
        </span>
      </h4>
      <h5 class="text-body-4xs text-ondark-secondary ml-5 inline-block truncate">
        {guide?.game ?? ''}
      </h5>
    </div>
  </div>
  <div class="flex min-w-0 items-center space-x-1.5">
    {#if toInteger(guide?.price) <= 0}
      <Tag variant="secondary" title="Free">Free</Tag>
    {/if}
    {#if isNew}
      <Tag variant="positive" class="capitlize hidden sm:inline-flex">New</Tag>
    {:else if isUpdated}
      <Tag variant="positive" class="capitlize hidden sm:inline-flex">Updated</Tag>
    {/if}
    <Tag variant="secondary" title="Average reading time">
      {humanizeDurationMinutes(guide?.total_reading_time_minutes || 1, { shorter: true, removeSpace: true })}
    </Tag>
  </div>
</a>
